/** @jsx jsx */
import { jsx } from '@emotion/core';

import React from 'react';
import { RouteComponentProps, navigate } from '@reach/router';
import { Grid, Divider, Typography } from '@material-ui/core';
import colors from '../../../../../themes/main-colors';
import { ProceedOrCancelButton } from '../../../../pge-plus-common/StepperForm';
import routes from '../../../../../routes';
import microcopyGroupIds from '../../../../pge-plus-common/microcopyGroupIds';
import useContentMgmt from '../../../../../hooks/useContentMgmt';
import Buttons from '../../../../buttons';
import { useRebateOnlyFormState } from '../../../../../providers/RebateOnlyFormStateProvider';
import {
  ApplicationErSectionType,
  ErEnrollmentPreference,
  SectionErRebateConfirm,
} from '../../../../../__generated__/pgeplus-types';
import { SaveAndComeBackLink } from '../../../../pge-plus-common/SaveAndComeBackLink';
import useUpdateERSections from '../../../../../hooks/pge-plus/useUpdateERSections';
import useUtil from '../../../../need-more-time-to-pay/useUtil';
import useWrapWithLoader from '../../../../../hooks/useWrapWithLoading';
import { rebateConfirmERFormStateAdapter } from '../rebateOnlyFormState.adapter';
import { handleInstructionsSectionErrors } from '../rebateOnlyForm.utils';

const VehicleInstructions: React.FC<RouteComponentProps> = (
  props: RouteComponentProps,
) => {
  const {
    formState: { rebateConfirm, application, rebateInfo },
    getSectionData,
    updateFormState,
  } = useRebateOnlyFormState();

  const { content, richText } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_REBATE_ONLY_CHARGER_INSTRUCTIONS,
  );

  const evPulseInstructions = [
    {
      step: 1,
      text: content.get('EVPULSE_CONNECTION_INSTRUCTIONS_1') || '',
    },
    {
      step: 2,
      text: content.get('EVPULSE_CONNECTION_INSTRUCTIONS_2') || '',
    },
  ];

  const sectionErRebateConfirm: SectionErRebateConfirm = getSectionData(
    ApplicationErSectionType.ErRebateConfirm,
  );

  const { updateSectionRebateConfirm } = useUpdateERSections();
  const { wrapWithLoader } = useWrapWithLoader();
  const { setErrorNotification } = useUtil();

  const onSubmit = wrapWithLoader(async (buttonType: string) => {
    try {
      const response = await updateSectionRebateConfirm(
        rebateConfirmERFormStateAdapter().fromFormState(
          {
            ...rebateConfirm,
            chargerActivatedInAppConfirmed: buttonType === 'WORKED',
          },
          application?.id!,
          sectionErRebateConfirm?.id,
        ),
      );

      const result = response?.data?.updateSectionERRebateConfirm;

      if (response?.errors || result?.validation?.errors) {
        throw new Error();
      }

      if (result?.section.data) {
        const updatedValues = rebateConfirmERFormStateAdapter().toFormState(
          result.section.data,
        );
        updateFormState('rebateConfirm', { ...updatedValues });
      }

      if (
        result?.section.errors.length &&
        // CUP-24446: "Didn't work" sends the customer back to UploadVIN.tsx, the real screen they came through
        handleInstructionsSectionErrors(
          result?.section.errors,
          routes.PGE_PLUS_REBATE_ONLY_UPLOAD_VIN,
        )
      ) {
        return;
      }

      if (!result?.section.errors.length) {
        // CUP-24447: "It Worked!" leads to the new vehicle rebate results screen, replacing the legacy EVPulse results page
        void navigate(routes.PGE_PLUS_REBATE_ONLY_VEHICLE_REBATE_RESULTS);
      } else {
        throw new Error();
      }
    } catch (e) {
      setErrorNotification(true);
    }
  });

  const getBackBtnRoute = () => {
    // CUP-24446: EVPULSE_ELIGIBLE is no longer part of the vehicle path.. Back goes to UploadVIN instead
    if (
      rebateInfo.chargerValidation?.isQualifiedProduct &&
      !rebateInfo.enrollmentPreference
    ) {
      return routes.PGE_PLUS_REBATE_ONLY_UPLOAD_VIN;
    }
    // CUP-24446: Both-qualified charger path, VIN captured via ConnectChargerToWifi.. came through Eligibility Confirmation
    if (rebateInfo.enrollmentPreference === ErEnrollmentPreference.UseMyCharger) {
      return routes.PGE_PLUS_REBATE_ONLY_ELIGIBILITY_CONFIRMATION;
    }
    // CUP-24664: Vehicle path came from ConnectVehicleToApp, go one page back there instead of two back to Eligibility Confirmation
    return routes.PGE_PLUS_REBATE_ONLY_CONNECT_VEHICLE;
  };

  const handleSaveAndExit = wrapWithLoader(async () => {
    try {
      const response = await updateSectionRebateConfirm({
        ...rebateConfirmERFormStateAdapter().fromFormState(
          {
            ...rebateConfirm,
            chargerActivatedInAppConfirmed: undefined,
          },
          application?.id!,
          sectionErRebateConfirm?.id,
        ),
        saveAndComeBack: true,
      });
      if (
        response?.errors ||
        response.data?.updateSectionERRebateConfirm?.validation?.errors ||
        response.data?.updateSectionERRebateConfirm?.section?.errors.length !==
          0
      ) {
        throw new Error();
      } else {
        void navigate(routes.ACCOUNT);
      }
    } catch {
      setErrorNotification(true);
    }
  });

  return (
    <Grid
      container
      css={{
        rowGap: 30,
      }}
    >
      <Grid item>
        <Typography
          variant={'h2'}
          css={{
            fontSize: '24px',
            fontWeight: 700,
          }}
        >
          {content.get('CHARGER_INSTRUCTIONS_LETS_CONNECT_TITLE')}
        </Typography>
      </Grid>
      <Grid item>
        <Typography
          variant={'body1'}
          css={{
            fontSize: '20px !important',
            lineHeight: '26px !important',
          }}
        >
          {content.get('EVPULSE_INSTRUCTIONS_SUB_HEADER')}
        </Typography>
      </Grid>
      <Grid item container>
        <Typography
          variant={'h2'}
          css={{
            fontSize: '20px',
            textAlign: 'center',
          }}
        >
          {content.get('CHARGER_INSTRUCTIONS_HERE_HOW_TEXT')}
        </Typography>
      </Grid>
      <Grid item container>
        {evPulseInstructions.map(step => {
          return (
            <Grid
              container
              wrap={'nowrap'}
              xs={12}
              css={{
                backgroundColor: colors.white,
                padding: '20px 30px',
                columnGap: 30,
                display: 'flex',
              }}
            >
              <Grid
                item
                css={{
                  display: 'flex',
                  borderRadius: '50%',
                  backgroundColor: colors.noirBlurLight,
                  height: 30,
                  padding: '3px 10px',
                  marginTop: 2,
                }}
              >
                <Typography
                  variant={'h2'}
                  css={{
                    color: colors.white,
                    fontSize: '20px',
                    textAlign: 'center',
                  }}
                >
                  {step.step}
                </Typography>
              </Grid>
              <Grid item container xs={12}>
                <Grid item>
                  <Typography
                    variant={'body1'}
                    css={{
                      fontSize: '20px',
                      '& p:first-child': {
                        margin: 0,
                      },
                      '& p:last-child': {
                        marginBottom: 0,
                      },
                      '& a': {
                        textDecoration: 'none',
                        fontWeight: 700,
                      },
                    }}
                  >
                    {richText(step.text)}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          );
        })}
      </Grid>
      <Grid
        item
        container
        spacing={2}
        css={{
          marginTop: 20,
          justifyContent: 'center',
        }}
      >
        <Grid item xs={6} md={3}>
          <Buttons
            type="button"
            fullWidth
            variant="outlined"
            color="secondary"
            css={{
              fontSize: '18px',
              borderRadius: '30px',
              '.MuiButton-label': {
                fontWeight: 500,
              },
            }}
            onClick={async () => {
              await onSubmit('DIDNT_WORK');
            }}
          >
            {content.get('BUTTON_DIDNT_WORK')}
          </Buttons>
        </Grid>
        <Grid item xs={6} md={3}>
          <Buttons
            type="button"
            fullWidth
            variant="contained"
            css={{
              fontSize: '18px',
              borderRadius: '30px',
              '.MuiButton-label': {
                fontWeight: 500,
              },
            }}
            onClick={async () => {
              await onSubmit('WORKED');
            }}
          >
            {content.get('BUTTON_IT_WORKED')}
          </Buttons>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider css={{ color: colors.gray93, margin: '20px 0 0px 0' }} />
        <ProceedOrCancelButton
          back={getBackBtnRoute()}
          backRouteState={
            rebateInfo.enrollmentPreference ===
            ErEnrollmentPreference.UseMyCharger
              ? {
                  message: 'EV_PULSE_REBATE_OFFER_DETAILS',
                  backButtonRoute: routes.PGE_PLUS_REBATE_ONLY_UPLOAD_VIN,
                  nextButtonRoute:
                    routes.PGE_PLUS_REBATE_ONLY_VEHICLE_INSTRUCTIONS,
                }
              : {}
          }
          next={false}
        />
      </Grid>
      <Grid item xs={12}>
        <SaveAndComeBackLink
          content={content}
          onSaveAndExit={handleSaveAndExit}
        />
      </Grid>
    </Grid>
  );
};

export default VehicleInstructions;
